import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { makeStyles } from '../../../tools/makeStyles';
import { keyframes } from 'tss-react';
import { getIconUrl } from '../utils/getIconUrl';
import { YES_NO, yesNoIcons } from './constants/constants';
import Header from '../common/Header.jsx';
import Footer from '../common/Footer.jsx';
import { defaultDescription, defaultTitle } from '../constants/constants';
import { setTabIndex } from '../../helpers/accessibility_helper';
import { useNumberKeySelectorCallback } from '../../helpers/number_key_selector_helper';
import { useKeySelect } from '../../helpers/key_select_helper';
import { DEFAULT_TRANSLATION } from '../../helpers/constants';

const blinker = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const useStyles = makeStyles((theme) => ({
  icon: {
    [theme.breakpoints.down('lg')]: {
      height: '3.6em',
    },
    [theme.breakpoints.up('lg')]: {
      height: '4em',
    },
  },
  iconButton: {
    cursor: 'pointer',
    paddingRight: '8px',
    outline: 'unset',
  },
  iconContainer: {
    padding: theme.spacing(1, 0),
  },
  blink: {
    animation: `${blinker} 200ms linear 0s 2`,
  },
}));

const start = 1;
const finish = 3;

const YesNoPreview = ({
  questionId = -1,
  showSkipButton = false,
  handleAnswer = () => {},
  onCommentChange = () => {},
  showTextComment = false,
  iconType = yesNoIcons[0].id,
  savedAnswer,
  isRequired = false,
  showDescription = false,
  title = defaultTitle,
  titleRef,
  titleAriaLabel,
  commentAriaLabel,
  description = defaultDescription,
  allowKeyboardSelection = true,
  isActive = true,
  commentsLabel,
  commentsPlaceholder,
  translation = DEFAULT_TRANSLATION,
}) => {
  const styles = useStyles();

  const emptyAnswer = {
    value: null,
    questionId,
    comment: '',
  };

  const [iconsArray, setIcons] = useState([]);
  const [answer, setAnswer] = useState(savedAnswer || emptyAnswer);
  const [, selectAnswer] = useState(false);

  useEffect(() => {
    if (savedAnswer && Object.entries(savedAnswer).length > 0) {
      const newIcons = getIconsFromS3(savedAnswer.value, iconType);
      setIcons(newIcons);
    } else {
      const newIcons = getIconsFromS3(-1, iconType);
      setIcons(newIcons);
    }
  }, [iconType, savedAnswer]);

  const handleMouseEnter = (index, iconType) => (event) => {
    event.preventDefault();
    const newIcons = getIconsFromS3(index, iconType);
    setIcons(newIcons);
  };

  const handleMouseLeave = (answer, iconType) => (event) => {
    event.preventDefault();
    if (!answer) {
      const defaultIcons = getIconsFromS3(-1, iconType);
      setIcons(defaultIcons);
    } else {
      const resetIcons = getIconsFromS3(answer.value, iconType);
      setIcons(resetIcons);
    }
  };

  const handleOptionalComment = (event) => {
    const newAnswer = { ...answer, comment: event.target.value };
    setAnswer(newAnswer);
    onCommentChange(event.target.value);
  };

  const numberKeySelector = useNumberKeySelectorCallback(0, 1, (key) => {
    handleAnswerSelection(key + 1, iconType);
  });

  useKeySelect(allowKeyboardSelection, numberKeySelector);

  const handleAnswerSelection = (index, iconType) => {
    reColorIcons(index, iconType);
    const newAnswer = { ...answer, value: index };
    setAnswer(newAnswer);
    selectAnswer(true);
    handleAnswer(newAnswer, !showTextComment);
  };

  const onConfirm = (hasAnswer = false) => {
    if (hasAnswer) {
      handleAnswer(answer);
    } else {
      setAnswer(emptyAnswer);
      selectAnswer(true);
      handleAnswer(emptyAnswer);
    }
  };

  const reColorIcons = (index, iconType) => {
    const newIcons = getIconsFromS3(index, iconType);
    setIcons(newIcons);
  };

  const getIconsFromS3 = (limit, iconType) => {
    const arr = [];
    for (let i = start; i < finish; i++) {
      arr.push(getIconUrl(i, YES_NO, iconType, i === limit));
    }
    return arr;
  };

  return (
    <div>
      <Header
        isRequired={isRequired}
        title={title}
        titleRef={titleRef}
        titleAriaLabel={titleAriaLabel}
        description={description}
        showDescription={showDescription}
        isActive={isActive}
        questionId={questionId}
        translation={translation}
      />

      <Grid container direction="row">
        {iconsArray.map((iconUrl, index) => {
          const position = index + 1;

          return (
            <Button
              className={classNames(answer && position === answer.value && styles.blink, styles.iconButton)}
              key={index}
              onClick={() => handleAnswerSelection(position, iconType)}
              onMouseOver={handleMouseEnter(position, iconType)}
              onMouseLeave={handleMouseLeave(answer, iconType)}
              tabIndex={setTabIndex(isActive)}
              aria-hidden={!isActive}
              aria-label={`Click to select ${index === 0 ? 'No' : 'Yes'}`}
              aria-pressed={position === answer?.value}
            >
              <img className={styles.icon} src={iconUrl} alt={`${iconType}-${index}`} />
            </Button>
          );
        })}
      </Grid>

      <Footer
        value={answer.value}
        showSkipButton={showSkipButton && answer.value === null}
        showOkButton={showTextComment && answer.value !== null}
        onConfirm={onConfirm}
        showTextComment={showTextComment}
        commentAriaLabel={commentAriaLabel}
        optionalComment={answer.comment}
        onChangeComment={handleOptionalComment}
        isActive={isActive}
        label={commentsLabel}
        placeholder={commentsPlaceholder}
        translation={translation}
      />
    </div>
  );
};

YesNoPreview.propTypes = {
  questionId: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  iconType: PropTypes.string,
  handleAnswer: PropTypes.func,
  onCommentChange: PropTypes.func,
  savedAnswer: PropTypes.object,
  showSkipButton: PropTypes.bool,
  showTextComment: PropTypes.bool,
  allowKeyboardSelection: PropTypes.bool,
  isRequired: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default YesNoPreview;
